<template>
  <v-dialog
    v-model="dialog"
    max-width="520"
    @after-leave="onClosed"
  >
    <v-card>
      <v-card-title class="d-flex align-center bg-primary">
        {{ t("feedback.prompt.title") }}
        <v-spacer />
        <v-btn
          icon
          variant="text"
          @click="close('later')"
        >
          <v-icon>{{ mdiClose }}</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="text-body-1 pt-4">
        {{ t("feedback.prompt.text") }}
      </v-card-text>

      <v-card-actions>
        <v-btn
          variant="text"
          size="small"
          @click="close('never')"
        >
          {{ t("feedback.prompt.never") }}
        </v-btn>
        <v-spacer />
        <v-btn
          variant="text"
          @click="close('later')"
        >
          {{ t("feedback.prompt.later") }}
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          @click="close('give')"
        >
          {{ t("feedback.prompt.give") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue"
import { useI18n } from "vue-i18n"
import { mdiClose } from "@mdi/js"
import { useFeedbackPrompt } from "@/composables/useFeedbackPrompt"
import { useAnalytics } from "@/composables/useAnalytics"

// Vraagt bij het openen van de app om feedback (hooguit eens per 30 dagen, zie
// useFeedbackPrompt). Wacht tot er geen andere dialoog of melding openstaat, zodat
// hij niet over de changelog of het nieuwe-collega's-venster heen valt.

const START_DELAY_MS = 4000
const POLL_INTERVAL_MS = 2000
const MAX_WAIT_MS = 2 * 60 * 1000

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { shouldShowPrompt, markPromptShown, dismissPromptForever } = useFeedbackPrompt()
const { trackCustom } = useAnalytics()

const dialog = ref(false)
let timer: ReturnType<typeof setTimeout> | undefined
let action: "give" | "later" | "never" = "later"

const isOnFeedbackPage = () => route.path.startsWith("/feedback")
const otherOverlayOpen = () => document.querySelector(".v-overlay--active") !== null

const tryOpen = (waitedMs: number) => {
  if (isOnFeedbackPage() || !shouldShowPrompt()) return
  if (otherOverlayOpen()) {
    if (waitedMs < MAX_WAIT_MS) {
      timer = setTimeout(() => tryOpen(waitedMs + POLL_INTERVAL_MS), POLL_INTERVAL_MS)
    }
    return
  }
  markPromptShown()
  action = "later"
  dialog.value = true
}

const close = (chosen: typeof action) => {
  action = chosen
  dialog.value = false
}

// Ook bij klikken buiten de dialoog of Escape (dan blijft action "later")
const onClosed = () => {
  if (action === "never") dismissPromptForever()
  trackCustom("feedback_prompt", { action })
  if (action === "give") router.push({ path: "/feedback", query: { from: route.path } })
}

onMounted(() => {
  timer = setTimeout(() => tryOpen(0), START_DELAY_MS)
})

onBeforeUnmount(() => clearTimeout(timer))
</script>
